import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Disc3 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSettings } from "../context/SettingsContext";
import api from "../utils/api";

function VersionBadge() {
  const [version, setVersion] = useState(null);
  useEffect(() => {
    api.get("/health").then(r => setVersion(r.data?.version)).catch(() => {});
  }, []);
  if (!version) return null;
  return <span className="text-zinc-800 text-xs hidden sm:block">v{version}</span>;
}
import { useAuth } from "../context/AuthContext";
import { usePublicAuth } from "../context/PublicAuthContext";
import { getLocalizedText } from "../utils/localizedText";

const SOCIAL_ICONS = {
  tiktok: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.17 8.17 0 0 0 4.78 1.52V6.78a4.85 4.85 0 0 1-1.01-.09z"/>
    </svg>
  ),
  instagram: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
      <circle cx="12" cy="12" r="4"/>
      <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none"/>
    </svg>
  ),
  facebook: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  youtube: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  ),
  spotify: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15">
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
    </svg>
  ),
  soundcloud: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15">
      <path d="M1.175 12.225c-.015-.135-.026-.27-.026-.406 0-1.98 1.608-3.588 3.59-3.588.3 0 .59.036.87.102.405-2.124 2.255-3.728 4.488-3.728 2.52 0 4.56 2.04 4.56 4.56 0 .15-.007.3-.021.449H15c1.65 0 2.99 1.34 2.99 2.99 0 1.654-1.34 2.993-2.99 2.993H2.37C1.065 15.598 0 14.53 0 13.228c0-1.12.734-2.072 1.175-1.003z"/>
    </svg>
  ),
  twitter: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  ),
  twitch: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15">
      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
    </svg>
  ),
  website: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="15" height="15">
      <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  ),
};

// Subtle dot-grid pattern for footer texture
const DOT_PATTERN = `url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='1.5' cy='1.5' r='1.5' fill='%23ffffff' fill-opacity='0.035'/%3E%3C/svg%3E")`;

export default function Footer() {
  const { settings } = useSettings();
  const { user } = useAuth();
  const { publicUser } = usePublicAuth();
  const { t, i18n } = useTranslation();
  const year = new Date().getFullYear();
  const socialLinks = settings?.social_links || [];
  const affiliateLogos = (settings?.affiliate_logos || []).filter(l => l.image_url || l.link_url);

  // Gravatar-sicheres Avatar-URL
  const avatarUrl = publicUser?.avatar_url && !publicUser.avatar_url.includes("gravatar.com")
    ? publicUser.avatar_url
    : null;
  const initials = (publicUser?.username || user?.name || "?").slice(0, 2).toUpperCase();

  const displayLinks = socialLinks.length > 0 ? socialLinks : [
    { platform: "tiktok", url: "https://www.tiktok.com/@huwndofficial", label: "TikTok" },
    { platform: "instagram", url: "https://www.instagram.com/huwndofficial", label: "Instagram" },
    { platform: "facebook", url: "https://www.facebook.com/huwnd/", label: "Facebook" },
  ];

  return (
    <footer
      className="relative border-t border-white/[0.06] overflow-hidden"
      style={{ backgroundColor: "#0a0a0a", backgroundImage: DOT_PATTERN }}
    >
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-rose-600/20" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">

          {/* ── Col 1: Brand ── */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2.5 mb-5 w-fit group">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt={settings?.site_name || "HUWND"} className="h-9 w-auto object-contain transition-opacity group-hover:opacity-80" />
              ) : (
                <>
                  <div className="w-9 h-9 bg-rose-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-black text-sm font-heading">H</span>
                  </div>
                  <span className="font-black text-white text-lg tracking-tight font-heading">HUWND</span>
                </>
              )}
            </Link>
            <p className="text-zinc-500 text-sm leading-relaxed max-w-sm mb-5">
              {getLocalizedText(settings?.footer_text, i18n.language) || "Hands Up Will Never Die – Die Community für Hands Up Musik."}
            </p>

            {/* Artist CTA */}
            <div className="bg-zinc-900/50 border border-white/[0.05] rounded-2xl px-4 py-3.5 mb-3">
              <div className="flex items-start gap-3">
                {/* Eyecatcher: Artist Badge */}
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-blue-500/15 border border-blue-500/25 flex items-center justify-center mt-0.5">
                  <Disc3 size={16} className="text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-zinc-200 text-sm font-bold mb-0.5">{t("footer.are_you_artist")}</p>
                  <p className="text-zinc-600 text-xs mb-2 leading-relaxed">{t("footer.artist_apply_desc")}</p>
                  <Link to="/artist-studio" className="inline-flex items-center gap-1 text-xs font-bold text-blue-400 hover:text-blue-300 transition-colors">
                    {t("footer.artist_studio_link")}
                  </Link>
                </div>
              </div>
            </div>

            {/* Label CTA */}
            <div className="bg-zinc-900/50 border border-white/[0.05] rounded-2xl px-4 py-3.5 mb-5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center mt-0.5"
                  style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.25)" }}>
                  <Disc3 size={16} style={{ color: "#10b981" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-zinc-200 text-sm font-bold mb-0.5">Du hast ein Label?</p>
                  <p className="text-zinc-600 text-xs mb-2 leading-relaxed">Vermarktest du Hands-Up Musik oder bist A&amp;R Manager? Manage Releases und Artists im Label Studio.</p>
                  <Link to="/label-studio" data-testid="footer-fuer-labels" className="inline-flex items-center gap-1 text-xs font-bold hover:text-green-300 transition-colors" style={{ color: "#10b981" }}>
                    Zum Label Studio →
                  </Link>
                </div>
              </div>
            </div>

            {/* Social icons */}
            <div className="flex items-center gap-2 flex-wrap">
              {displayLinks.map((link, i) => {
                const Icon = SOCIAL_ICONS[link.platform] || SOCIAL_ICONS.website;
                return (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                    data-testid={`footer-${link.platform}`}
                    title={link.label || link.platform}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] hover:bg-white/[0.09] text-zinc-500 hover:text-white border border-white/[0.06] transition-all duration-200 hover:scale-110 hover:border-white/20">
                    <Icon />
                  </a>
                );
              })}
            </div>
          </div>

          {/* ── Col 2: Navigation ── */}
          <div>
            {(() => {
              const footerLinks = settings?.footer_links || [];
              const navLinks = footerLinks.filter(l => l.group === "nav");
              const legalLinks = footerLinks.filter(l => l.group === "legal");

              const renderLink = (link, i) => {
                const cls = "text-zinc-500 hover:text-white text-sm transition-colors hover:translate-x-0.5 inline-block duration-150";
                return link.is_external ? (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className={cls}>
                    {link.label}
                  </a>
                ) : (
                  <Link key={i} to={link.url} className={cls}>
                    {link.label}
                  </Link>
                );
              };

              const defaultNav = [
                { label: "Musik", url: "/search", is_external: false },
                { label: "Artists", url: "/artists", is_external: false },
                { label: "Community", url: "/community", is_external: false },
              ];
              const defaultLegal = [
                { label: "Impressum", url: "/page/impressum", is_external: false },
                { label: "Datenschutz", url: "/page/datenschutz", is_external: false },
              ];

              const shownNav = navLinks.length > 0 ? navLinks : defaultNav;
              const shownLegal = legalLinks.length > 0 ? legalLinks : defaultLegal;

              return (
                <>
                  <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest mb-5">{t("footer.navigation")}</p>
                  <nav className="flex flex-col gap-2.5">
                    {shownNav.map(renderLink)}
                  </nav>
                  <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest mt-7 mb-5">{t("footer.legal")}</p>
                  <nav className="flex flex-col gap-2.5">
                    {shownLegal.map(renderLink)}
                    {/* Cookie-Einstellungen – CCM19 natives onclick-Snippet */}
                    <span
                      data-testid="footer-cookie-settings"
                      dangerouslySetInnerHTML={{
                        __html: '<a href="#" onclick="CCM.openWidget(); return false;" class="text-xs" style="color:#71717a;text-decoration:none" onmouseover="this.style.color=\'#d4d4d8\'" onmouseout="this.style.color=\'#71717a\'">Cookie-Einstellungen</a>'
                      }}
                    />
                  </nav>
                </>
              );
            })()}
          </div>

          {/* ── Col 3: Partner / Affiliate ── */}
          <div>
            {affiliateLogos.length > 0 && (
              <div className="flex flex-col gap-4 mt-1">
                {affiliateLogos.slice(0, 2).map((logo, i) => (
                  logo.link_url ? (
                    <a key={i} href={logo.link_url} target="_blank" rel="noopener noreferrer sponsored"
                      title={logo.title || "Partner"}
                      data-testid={`footer-affiliate-${i}`}>
                      <img src={logo.image_url} alt={logo.title || "Partner"} className="max-w-full h-auto" />
                    </a>
                  ) : (
                    <img key={i} src={logo.image_url} alt={logo.title || "Partner"} className="max-w-full h-auto" />
                  )
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="mt-12 pt-6 border-t border-white/[0.05] flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <p className="text-zinc-700 text-xs">&copy; {year} Hands Up Will Never Die</p>
            <span className="text-zinc-800 text-xs hidden sm:block">·</span>
            <span className="text-zinc-800 text-xs hidden sm:block">{t("footer.all_rights")}</span>
            <span className="text-zinc-800 text-xs hidden sm:block">·</span>
            <VersionBadge />
          </div>

          <div className="flex items-center gap-4">
            {user && (
              <div className="flex items-center gap-3" data-testid="footer-user-bar">
                <div className="flex items-center gap-1.5">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={publicUser?.username}
                      className="w-4 h-4 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-4 h-4 rounded-full bg-rose-600/80 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold leading-none" style={{ fontSize: "9px" }}>
                        {initials[0]}
                      </span>
                    </div>
                  )}
                  <span className="text-zinc-600 text-xs">{user.username}</span>
                </div>
                <span className="text-zinc-800 text-xs">·</span>
                <Link to="/admin/dashboard" data-testid="footer-admin-link"
                  className="text-zinc-600 hover:text-zinc-400 text-xs transition-colors">
                  {t("footer.dashboard")}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
